/**
 * Clip Export
 *
 * Generates a GLB blob containing one or more animation clips built from
 * transform keyframes. Uses an isolated scratch scene so that only
 * the clip data ends up in the GLB — no stray preview groups or other
 * scene content leak in.
 */

import { Scene } from "@babylonjs/core/scene";
import { TransformNode } from "@babylonjs/core/Meshes/transformNode";
import { AnimationGroup } from "@babylonjs/core/Animations/animationGroup";
import { Animation as BabylonAnimation } from "@babylonjs/core/Animations/animation";
import { Vector3, Quaternion } from "@babylonjs/core/Maths/math.vector";
import { GLTF2Export } from "@babylonjs/serializers";

import type { TransformKeyframe } from "../scenedata/behaviors/transformkeyframe";
import { isEditorHelperMesh } from "./exportFilters";

const EXPORT_NAME = "clip";

export interface ClipData {
    name: string;
    keyframes: TransformKeyframe[];
    fps: number;
}

/**
 * Build a Babylon AnimationGroup from keyframes, targeting the given node.
 */
function buildGroup(
    clip: ClipData,
    target: TransformNode,
    scene: Scene,
): AnimationGroup {
    const group = new AnimationGroup(clip.name, scene);
    const sorted = [...clip.keyframes].sort((a, b) => a.time - b.time);

    // Position
    const posKeys = sorted.filter(kf => kf.position);
    if (posKeys.length > 0) {
        const anim = new BabylonAnimation(
            "position", "position", clip.fps,
            BabylonAnimation.ANIMATIONTYPE_VECTOR3,
            BabylonAnimation.ANIMATIONLOOPMODE_CYCLE
        );
        anim.setKeys(posKeys.map(kf => ({
            frame: kf.time * clip.fps,
            value: new Vector3(kf.position![0], kf.position![1], kf.position![2])
        })));
        group.addTargetedAnimation(anim, target);
    }

    // Rotation (quaternion)
    const rotKeys = sorted.filter(kf => kf.rotation);
    if (rotKeys.length > 0) {
        const anim = new BabylonAnimation(
            "rotationQuaternion", "rotationQuaternion", clip.fps,
            BabylonAnimation.ANIMATIONTYPE_QUATERNION,
            BabylonAnimation.ANIMATIONLOOPMODE_CYCLE
        );
        anim.setKeys(rotKeys.map(kf => ({
            frame: kf.time * clip.fps,
            value: new Quaternion(kf.rotation![0], kf.rotation![1], kf.rotation![2], kf.rotation![3])
        })));
        group.addTargetedAnimation(anim, target);
    }

    // Scale
    const scaleKeys = sorted.filter(kf => kf.scale);
    if (scaleKeys.length > 0) {
        const anim = new BabylonAnimation(
            "scaling", "scaling", clip.fps,
            BabylonAnimation.ANIMATIONTYPE_VECTOR3,
            BabylonAnimation.ANIMATIONLOOPMODE_CYCLE
        );
        anim.setKeys(scaleKeys.map(kf => ({
            frame: kf.time * clip.fps,
            value: new Vector3(kf.scale![0], kf.scale![1], kf.scale![2])
        })));
        group.addTargetedAnimation(anim, target);
    }

    return group;
}

/**
 * Generate a GLB blob containing one or more animation clips.
 *
 * @param clips - Array of clip data (name + keyframes + fps)
 * @param scene - The Babylon.js scene (used only to obtain the engine)
 * @returns GLB blob ready to store in ResourceManager
 */
export async function generateClipsGLB(
    clips: ClipData[],
    scene: Scene,
): Promise<Blob> {
    const exportScene = new Scene(scene.getEngine());
    // Use right-handed coordinates to match the main scene.  This prevents
    // the GLTF serializer from applying LH→RH conversion on animation data,
    // so values survive the export→import round-trip unchanged.
    exportScene.useRightHandedSystem = true;

    const target = new TransformNode("__clip_root__", exportScene);
    const groups: AnimationGroup[] = [];

    for (const clip of clips) {
        groups.push(buildGroup(clip, target, exportScene));
    }

    try {
        const glb = await GLTF2Export.GLBAsync(
            exportScene,
            EXPORT_NAME,
            {
                exportWithoutWaitingForScene: true,
                removeNoopRootNodes: false,
                // The scratch scene holds only nodes built above, but the
                // exclusion must hold for every export call site even if that
                // construction grows.
                shouldExportNode: (node) => !isEditorHelperMesh(node),
            }
        );

        const glbFile = glb.glTFFiles[`${EXPORT_NAME}.glb`];
        if (glbFile instanceof Blob) {
            return glbFile;
        }
        return new Blob([glbFile], { type: "model/gltf-binary" });
    } finally {
        for (const g of groups) g.dispose();
        target.dispose();
        exportScene.dispose();
    }
}

/**
 * Generate a GLB blob containing a single animation clip built from keyframes.
 * Convenience wrapper around generateClipsGLB.
 *
 * @param keyframes - Array of transform keyframes (position/rotation/scale at time)
 * @param fps - Frames per second for the animation
 * @param scene - The Babylon.js scene (used only to obtain the engine)
 * @param clipName - Name for the animation group inside the GLB (default: "Clip")
 * @returns GLB blob ready to store in ResourceManager
 */
export async function generateClipGLB(
    keyframes: TransformKeyframe[],
    fps: number,
    scene: Scene,
    clipName: string = "Clip",
): Promise<Blob> {
    return generateClipsGLB([{ name: clipName, keyframes, fps }], scene);
}
